import { useCallback, useEffect, useRef, useState } from "react"

import { useLiveDeviceTelemetry } from "@common/hooks/livekit/useLiveDeviceTelemetry"
import { MEDIA_ACCESS_HOW, requestMediaAccess } from "../../engine/live/tutor/tutorMediaAccess"

/**
 * Le tuteur au natif, à l'entrée de l'ancien écran du live : le micro puis la caméra demandés AVANT que la
 * salle ne se connecte, sans rien capturer (`requestMediaAccess`, la même règle et les mêmes traces
 * `media_access` que la nouvelle salle).
 *
 * Sous Android, à la première entrée, la salle recevait son jeton dès le premier relevé et publiait pendant
 * les questions du système, et la capture de test de `PermissionsManager` ouvrait la même caméra : son
 * `track.stop()` tenait l'exécuteur WebRTC dans `stopCapture`, et le fil JS restait bloqué sur
 * `addTransceiver`, l'écran figé jusqu'au kill.
 *
 * `enabled` : les questions sont posées, et `gate(apply)` retient le dernier relevé jusqu'à leur réponse,
 * puis l'applique. Sans `enabled`, `apply` passe tout de suite. Une réponse, même un refus ou une lecture
 * impossible, ouvre la salle, comme avant ; sans réponse au bout de `MEDIA_ACCESS_WAIT_MS` aussi : au pire,
 * la salle se connecte pendant les questions, comme avant, et le tuteur n'est jamais tenu hors de son live.
 */
export const MEDIA_ACCESS_WAIT_MS = 30 * 1000

export function useTutorMediaAccessGate(enabled, apply) {
    const telemetry = useLiveDeviceTelemetry()
    const [asked, setAsked] = useState(false)
    const answered = !enabled || asked
    const heldRef = useRef(null)

    useEffect(() => {
        if (!enabled) return
        let mounted = true
        const startedAt = Date.now()
        telemetry.transition("media_unchecked", "media_requesting", "media_access", { how: MEDIA_ACCESS_HOW })
        const waited = setTimeout(() => {
            telemetry.transition("media_requesting", "media_wait_over", "media_access", {
                how: MEDIA_ACCESS_HOW,
                durationMs: Date.now() - startedAt,
            })
            if (mounted) setAsked(true)
        }, MEDIA_ACCESS_WAIT_MS)
        requestMediaAccess()
            .then(
                (granted) =>
                    telemetry.transition("media_requesting", "media_checked", "media_access", {
                        how: MEDIA_ACCESS_HOW,
                        granted,
                        durationMs: Date.now() - startedAt,
                        unmounted: !mounted,
                    }),
                (cause) =>
                    telemetry.error("media_access", cause, { how: MEDIA_ACCESS_HOW, durationMs: Date.now() - startedAt })
            )
            .finally(() => {
                clearTimeout(waited)
                if (mounted) setAsked(true)
            })
        return () => {
            mounted = false
            clearTimeout(waited)
        }
    }, [enabled, telemetry])

    useEffect(() => {
        if (!answered || heldRef.current == null) return
        const held = heldRef.current
        heldRef.current = null
        apply(held)
    }, [answered, apply])

    return useCallback(
        (data) => {
            if (answered) apply(data)
            else heldRef.current = data
        },
        [answered, apply]
    )
}
